import { inject, Injectable } from '@angular/core';
import { AssocCallSignBase } from '../../models/assoc-call-sign-base';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ActionCode } from '../../../../../../core/models/resources/action-code';
import { ApplicationStateService } from '../../../../shared/services/application-state/application-state.service';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { ApplicationFormControlManagerService } from '../../../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { SchedulesDAndHSharedFormService } from '../../../shared/services/schedules-d-and-h-shared-form/schedules-d-and-h-shared-form.service';

@Injectable({
    providedIn: 'root',
})
export class CallSignFormService {
    private readonly controlsDisabledForDeletedCallSigns: (keyof AssocCallSignBase)[] =
        ['callSign'];

    private readonly applicationStateService = inject(ApplicationStateService);
    private readonly applicationFormControlManager = inject(
        ApplicationFormControlManagerService,
    );
    private readonly schedulesDAndHSharedFormService = inject(
        SchedulesDAndHSharedFormService,
    );

    getFormDefinition(): FormGroup<FormGroupType<AssocCallSignBase>> {
        return new FormGroup<FormGroupType<AssocCallSignBase>>({
            id: new FormControl<number | null>(null),
            actionCode: new FormControl<ActionCode | null>(
                ActionCode.Add,
                this.applicationStateService.IsExistingLicenseApplication()
                    ? undefined
                    : Validators.required,
            ),
            callSign: new FormControl<string | null>(null, [
                Validators.required,
                Validators.maxLength(10),
                Validators.pattern(RegExPattern.ScheduleHAssociatedCallSign),
            ]),
            isDeleted: new FormControl<boolean | null>(null),
        });
    }

    /**
     * Toggles the form controls based on the isDeleted value.
     * @param callSign the call sign form group to update
     * @param isDeleted true if the call sign is marked as deleted
     */
    toggleFormControlsForDeletedChange(
        callSign: FormGroup<FormGroupType<AssocCallSignBase>>,
        toggleOptions: { emitEvent?: boolean } = { emitEvent: true },
    ): void {
        const isDeleted = callSign.controls.isDeleted.value;
        this.controlsDisabledForDeletedCallSigns.forEach((controlName) => {
            const control = callSign.controls[controlName];

            if (isDeleted === true) {
                this.applicationFormControlManager.disable(
                    control,
                    toggleOptions,
                );
            } else {
                this.applicationFormControlManager.enable(
                    control,
                    toggleOptions,
                    () => {
                        control.markAsTouched();
                    },
                );
            }
        });
    }

    /**
     * Marks the call sign as deleted when applicable and toggles the controls accordingly to it's deleted state.
     * @param callSign
     * @returns
     */
    markCallSignAsDeletedAndToggleControls(
        callSign: FormGroup<FormGroupType<AssocCallSignBase>>,
        response: AssocCallSignBase[],
    ): void {
        // Don't need to toggle anything if the call sign is new
        // also when the deleted control has not been touched by the user and the call sign is not deleted
        if (
            callSign.controls.actionCode.value === ActionCode.Add ||
            (callSign.controls.isDeleted.pristine &&
                callSign.controls.isDeleted.value !== true)
        ) {
            return;
        }

        this.schedulesDAndHSharedFormService.refreshFormActionCode(
            callSign,
            response,
        );

        this.toggleFormControlsForDeletedChange(callSign);
    }
}
